import React, { useState } from "react";
import { FiLock, FiEye, FiEyeOff, FiShield } from "react-icons/fi";
import { auth } from "../../firebase";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import "./StaffChangePassword.css";

// Common passwords list for validation
const commonPasswords = [
  "password", "123456", "123456789", "qwerty", "abc123", "password123",
  "admin", "letmein", "welcome", "monkey", "1234567890", "password1"
];



const StaffChangePassword = ({ userData, isMandatory = false, isAdmin = false, onPasswordVerificationRequired = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  
  // Password fields
  const [passwordFields, setPasswordFields] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [passwordTouched, setPasswordTouched] = useState({
    new: false,
    confirm: false,
  });

  // Error states
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [passwordPolicyError, setPasswordPolicyError] = useState("");

  // Core validations (checkbox rules)
  const coreValidations = {
    length:
      passwordFields.new.length >= 8 &&
      passwordFields.new.length <= 24,
    uppercaseLowercase:
      /[A-Z]/.test(passwordFields.new) &&
      /[a-z]/.test(passwordFields.new),
    number: /\d/.test(passwordFields.new),
    special: /[!@#$%^&*]/.test(passwordFields.new),
  };

  const passwordStrength =
    [
      coreValidations.length,
      coreValidations.uppercaseLowercase,
      coreValidations.number,
      coreValidations.special
    ].filter(Boolean).length;

  // Error validations (red border rules)
  const errorValidations = {
    noSpaces: !/\s/.test(passwordFields.new),
    noCommon: !commonPasswords.includes(passwordFields.new),
    noRepeats: !/(.)\1\1/.test(passwordFields.new),
    match:
      passwordFields.new === passwordFields.confirm &&
      passwordFields.confirm.length > 0,
    notUsername:
      !userData?.fullName ||
      passwordFields.new.toLowerCase() !== userData.fullName.toLowerCase(),
    notOldPassword: passwordFields.new !== passwordFields.current,
  };

  // Final validity (button enable)
  const allValid =
    passwordFields.current.trim().length > 0 &&
    Object.values(coreValidations).every(Boolean) &&
    Object.values(errorValidations).every(Boolean);

  // Red border logic
  const newPasswordHasError =
    passwordTouched.new &&
    passwordFields.new.length > 0 &&
    (!coreValidations.length ||
      !coreValidations.uppercaseLowercase ||
      !coreValidations.number ||
      !coreValidations.special ||
      !errorValidations.noSpaces ||
      !errorValidations.noCommon ||
      !errorValidations.noRepeats ||
      !errorValidations.notUsername ||
      !errorValidations.notOldPassword);

  const confirmPasswordHasError =
    passwordTouched.confirm &&
    passwordFields.confirm.length > 0 && !errorValidations.match;

  // Handlers
  const handlePasswordCancel = () => {
    setPasswordFields({ current: "", new: "", confirm: "" });
    setPasswordTouched({ new: false, confirm: false });
    setCurrentPasswordError("");
    setPasswordPolicyError("");
  };

  const handlePasswordUpdate = async () => {
    if (!passwordFields.current.trim()) {
      setCurrentPasswordError("Current password is required.");
      return;
    }

    setIsPasswordSubmitting(true);
    setCurrentPasswordError("");
    setPasswordPolicyError("");

    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        setPasswordPolicyError("Session expired. Please log in again.");
        return;
      }

      const email = userData?.email || authUser.email || "";
      if (!email) {
        setPasswordPolicyError("Unable to determine your email address. Please log in again.");
        return;
      }

      // STEP 1: Verify current password against Firebase
      const credential = EmailAuthProvider.credential(email, passwordFields.current);
      await reauthenticateWithCredential(authUser, credential);

      // STEP 2: Store pending password change in localStorage
      const pendingPasswordChange = {
        uid: authUser.uid,
        email,
        currentPassword: passwordFields.current,
        newPassword: passwordFields.new,
        timestamp: Date.now(),
        type: 'password-change',
      };
      localStorage.setItem('pendingPasswordChange', JSON.stringify(pendingPasswordChange));

      // STEP 3: Trigger password verification via callback
      if (onPasswordVerificationRequired) {
        onPasswordVerificationRequired({
          uid: authUser.uid,
          email,
          currentPassword: passwordFields.current,
          newPassword: passwordFields.new,
        });
      } else {
        // No callback provided - show error
        setPasswordPolicyError("Password verification modal not available. Please ensure the onPasswordVerificationRequired callback is provided.");
      }
    } catch (err) {
      console.error('Password verification failed:', err);
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setCurrentPasswordError("Incorrect Password");
      } else {
        setPasswordPolicyError(err?.message || "Password verification failed.");
      }
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  // Live validation - show errors only when there is text input
  const showNewPasswordErrors = passwordFields.new.length > 0;
  const showConfirmPasswordErrors = passwordFields.confirm.length > 0;

  const strengthColors = [
    "#e53e3e",
    "#dd6b20",
    "#d69e2e",
    "#38a169"
  ];

  return (
    <div className={`staff-change-password ${isMandatory ? 'scp-modal-mode' : 'scp-profile-mode'}`}>
      {isMandatory ? (
        <>
          <div className="scp-modal-header">
            <div className="scp-header-icon">
              <FiShield />
            </div>
            <div className="scp-header-text">
              <h3 className="scp-modal-title">Change Password</h3>
              <p className="scp-modal-subtitle">
                For your account security, please set a new password before continuing.
              </p>
            </div>
          </div>
          <div className="scp-modal-body">
            <div className="scp-info-notice">
              <FiLock className="scp-info-icon" />
              <span>Your new password must be different from your previous passwords.</span>
            </div>

            {passwordPolicyError && (
              <div className="scp-alert-error">
                ⚠️ {passwordPolicyError}
              </div>
            )}

            {/* Current Password */}
            <div className="staff-form-row staff-password-row">
              <label>Current Password</label>
              <div className="staff-input-icon-wrapper staff-password-input-wrapper">
                <FiLock className="staff-input-icon" />
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  maxLength={64}
                  placeholder="Enter your current password"
                  value={passwordFields.current}
                  onChange={(e) => {
                    setPasswordFields({ ...passwordFields, current: e.target.value });
                    setCurrentPasswordError("");
                  }}
                  className={currentPasswordError ? "input-error" : ""}
                />
                <span
                  className="staff-eye-icon"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
              {currentPasswordError && (
                <div className="staff-field-error">
                  {currentPasswordError}
                </div>
              )}
            </div>

            {/* New Password */}
            <div className="staff-form-row staff-password-row">
              <label>New Password</label>
              <div className="staff-input-icon-wrapper staff-password-input-wrapper">
                <FiLock className="staff-input-icon" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  maxLength={24}
                  placeholder="Enter your new password"
                  value={passwordFields.new}
                  onChange={(e) => {
                    setPasswordFields({
                      ...passwordFields,
                      new: e.target.value,
                    });
                    if (!passwordTouched.new) setPasswordTouched(prev => ({ ...prev, new: true }));
                  }}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  className={newPasswordHasError ? "input-error" : ""}
                />
                <span
                  className="staff-eye-icon"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>

              {showNewPasswordErrors && !errorValidations.noSpaces && (
                <div className="staff-field-error">No spaces allowed</div>
              )}
              {showNewPasswordErrors && !errorValidations.noCommon && (
                <div className="staff-field-error">This is a common password</div>
              )}
              {showNewPasswordErrors && !errorValidations.noRepeats && (
                <div className="staff-field-error">No more than 2 repeated characters in a row</div>
              )}
              {showNewPasswordErrors && !errorValidations.notUsername && (
                <div className="staff-field-error">Password must not match your name</div>
              )}
              {showNewPasswordErrors && passwordFields.current.length > 0 && passwordFields.new === passwordFields.current && (
                <div className="staff-field-error">New password must be different from current password</div>
              )}

              <div className="staff-password-strength">
                <div className="password-strength-bar">
                  <span style={{ 
                    width: `${(passwordStrength / 4) * 100}%`,
                    background: passwordStrength > 0 ? strengthColors[passwordStrength - 1] : '#e0e0e0'
                  }} />
                </div>
                <div className="password-strength-text">
                  Use 8 or more characters with a mix of letters, numbers & symbols.
                </div>
              </div>

              <div className="scp-password-rules">
                <div className={`scp-rule-item ${coreValidations.length ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>8–24 characters long</span>
                </div>
                <div className={`scp-rule-item ${coreValidations.uppercaseLowercase ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>Uppercase & lowercase letters</span>
                </div>
                <div className={`scp-rule-item ${coreValidations.number ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>At least one number (0–9)</span>
                </div>
                <div className={`scp-rule-item ${coreValidations.special ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>Special character (!@#$%^&*)</span>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="staff-form-row staff-password-row">
              <label>Confirm New Password</label>
              <div className="staff-input-icon-wrapper staff-password-input-wrapper">
                <FiLock className="staff-input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  maxLength={24}
                  placeholder="Re-enter your new password"
                  value={passwordFields.confirm}
                  onChange={(e) =>
                    setPasswordFields({
                      ...passwordFields,
                      confirm: e.target.value,
                    })
                  }
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  className={confirmPasswordHasError ? "input-error" : ""}
                />
                <span
                  className="staff-eye-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>

              {showConfirmPasswordErrors && passwordFields.new.length > 0 && passwordFields.confirm !== passwordFields.new && (
                <div className="staff-field-error">Passwords must match</div>
              )}
            </div>

            <div className="staff-form-actions right-aligned scp-modal-actions">
              <button
                type="button"
                className="staff-update-btn"
                disabled={!allValid || isPasswordSubmitting}
                onClick={handlePasswordUpdate}
              >
                {isPasswordSubmitting ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="staff-section-header">
            <h3 className="staff-section-title">Change Password</h3>
          </div>

          <p className="staff-section-subheading">
            Your new password must be different from your previous passwords
          </p>

          <div className="staff-section-body">
            {/* Current Password */}
            <div className="staff-form-row staff-password-row">
              <label>Current Password</label>
              <div className="staff-input-icon-wrapper staff-password-input-wrapper">
                <FiLock className="staff-input-icon" />
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  maxLength={64}
                  placeholder="Enter your current password"
                  value={passwordFields.current}
                  onChange={(e) => {
                    setPasswordFields({ ...passwordFields, current: e.target.value });
                    setCurrentPasswordError("");
                  }}
                  className={currentPasswordError ? "input-error" : ""}
                />
                <span
                  className="staff-eye-icon"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
              {currentPasswordError && (
                <div className="staff-field-error">
                  {currentPasswordError}
                </div>
              )}
            </div>

            {/* New Password */}
            <div className="staff-form-row staff-password-row">
              {passwordPolicyError && (
                <div className="scp-alert-error">
                  ⚠️ {passwordPolicyError}
                </div>
              )}
              <label>New Password</label>
              <div className="staff-input-icon-wrapper staff-password-input-wrapper">
                <FiLock className="staff-input-icon" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  maxLength={24}
                  placeholder="Enter your new password"
                  value={passwordFields.new}
                  onChange={(e) => {
                    setPasswordFields({
                      ...passwordFields,
                      new: e.target.value,
                    });
                    if (!passwordTouched.new) setPasswordTouched(prev => ({ ...prev, new: true }));
                  }}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  className={newPasswordHasError ? "input-error" : ""}
                />
                <span
                  className="staff-eye-icon"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>

              {showNewPasswordErrors && !errorValidations.noSpaces && (
                <div className="staff-field-error">No spaces allowed</div>
              )}
              {showNewPasswordErrors && !errorValidations.noCommon && (
                <div className="staff-field-error">This is a common password</div>
              )}
              {showNewPasswordErrors && !errorValidations.noRepeats && (
                <div className="staff-field-error">No more than 2 repeated characters in a row</div>
              )}
              {showNewPasswordErrors && !errorValidations.notUsername && (
                <div className="staff-field-error">Password must not match your name</div>
              )}
              {showNewPasswordErrors && passwordFields.current.length > 0 && passwordFields.new === passwordFields.current && (
                <div className="staff-field-error">New password must be different from current password</div>
              )}

              <div className="staff-password-strength">
                <div className="password-strength-bar">
                  <span style={{ 
                    width: `${(passwordStrength / 4) * 100}%`,
                    background: passwordStrength > 0 ? strengthColors[passwordStrength - 1] : 'linear-gradient(to right, #3182ce, #4299e1, #63b3ed, #90cdf4)'
                  }} />
                </div>
                <div className="password-strength-text">
                  Use 8 or more characters with a mix of letters, numbers & symbols.
                </div>
              </div>

              <div className="scp-password-rules">
                <div className={`scp-rule-item ${coreValidations.length ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>8–24 characters long</span>
                </div>
                <div className={`scp-rule-item ${coreValidations.uppercaseLowercase ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>Uppercase & lowercase letters</span>
                </div>
                <div className={`scp-rule-item ${coreValidations.number ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>At least one number (0–9)</span>
                </div>
                <div className={`scp-rule-item ${coreValidations.special ? 'valid' : ''}`}>
                  <span className="scp-rule-dot" />
                  <span>Special character (!@#$%^&*)</span>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="staff-form-row staff-password-row">
              <label>Confirm New Password</label>
              <div className="staff-input-icon-wrapper staff-password-input-wrapper">
                <FiLock className="staff-input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  maxLength={24}
                  placeholder="Re-enter your new password"
                  value={passwordFields.confirm}
                  onChange={(e) =>
                    setPasswordFields({
                      ...passwordFields,
                      confirm: e.target.value,
                    })
                  }
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  className={confirmPasswordHasError ? "input-error" : ""}
                />
                <span
                  className="staff-eye-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>

              {showConfirmPasswordErrors && passwordFields.new.length > 0 && passwordFields.confirm !== passwordFields.new && (
                <div className="staff-field-error">Passwords must match</div>
              )}
            </div>

            <div className="staff-form-actions right-aligned">
              {!isMandatory && (
                <button
                  className="staff-cancel-btn"
                  onClick={handlePasswordCancel}
                  disabled={isPasswordSubmitting}
                >
                  Cancel
                </button>
              )}

              <button
                type="button"
                className="staff-update-btn"
                disabled={!allValid || isPasswordSubmitting}
                onClick={handlePasswordUpdate}
              >
                {isPasswordSubmitting ? "Updating..." : "Update Changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StaffChangePassword;
